import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { fixFilenameMojibake } from '@ideoz/core';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogHeader,
  Button,
  Checkbox,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Icon,
  type FileForSelection,
} from '@ideoz/ui';
import {
  FilePlus,
  FileSearch,
  Paperclip,
  Trash2,
  X,
  CheckCircle,
  XCircle,
  FileText,
  Info,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  RefreshCw,
} from 'lucide-react';
import {
  loadGoogleApis,
  openGoogleFilePicker,
  validateGDriveFiles,
  getSupportedFileTypesString,
  type GDriveFile,
  type FileValidationResult,
} from '@ideoz/core/utils/googleDrive.utils';
import { googleDriveService } from '@ideoz/core/api/services/googleDrive.service';
import {
  ALLOWED_ATTACHMENT_MIME_TYPES,
  MAX_ATTACHMENT_SIZE_BYTES,
} from '@ideoz/core/utils/attachment-types';

export interface ProjectAttachment {
  id: string;
  fileName: string;
  /** The API returns the name under `name` (sometimes `originalName`); keep both. */
  name?: string;
  originalName?: string;
  isLoading?: boolean;
  fromGoogle?: boolean;
  gdriveId?: string;
  mimeType?: string;
  fileSize?: number;
}

/**
 * Resolve a display filename off an attachment, tolerant of the API's key
 * (`fileName` | `name` | `originalName`) and repairing any UTF-8-as-Latin-1
 * mojibake. Used for both display and the local-uploading dedup, so the two
 * always agree on the same name.
 */
const resolveAttachmentName = (a: {
  fileName?: string;
  name?: string;
  originalName?: string;
}): string => fixFilenameMojibake(a.fileName ?? a.name ?? a.originalName ?? '');

export interface GoogleAuthConfig {
  CLIENT_ID: string;
  API_KEY: string;
  APP_ID: string;
  SCOPES: string;
}

export interface UploadFilesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectAttachments: ProjectAttachment[];
  onFilesUpload: (files: File[]) => void | Promise<void>;
  onFileDelete: (fileId: string) => Promise<void>;
  onFetchAttachments?: () => void;
  isUploading?: boolean;
  loadingAttachments?: boolean;
  showToast?: (options: {
    message: string;
    info?: string;
    severity: 'error' | 'success' | 'info';
  }) => void;
  t?: (key: string) => string;
  isWeb?: boolean;
  planDetails?: any;
  // Google Drive props
  googleAuthConfig?: GoogleAuthConfig;
  projectId?: string;
  onGoogleSyncStart?: (data: {
    folderId: string;
    folderName: string;
    accessToken: string;
    acceptedFilesCount: number;
    files: Array<{
      id: string;
      name: string;
      mimeType: string;
      size?: number;
      attachmentId?: string;
    }>;
  }) => void;
}

// Allow-lists live in @ideoz/core/utils/attachment-types — edit there to add
// or remove an extension; every dialog reads from that single source of truth.
const ALLOWED_FILE_TYPES = ALLOWED_ATTACHMENT_MIME_TYPES;
const MAX_FILE_SIZE = MAX_ATTACHMENT_SIZE_BYTES;

// MIME → friendly name. Kept for the existing error-message UX; covers every
// MIME we accept. If you add a new MIME above, add a friendly name here too.
const FILE_TYPE_NAMES: Record<string, string> = {
  'text/plain': 'TXT',
  'text/csv': 'CSV',
  'text/markdown': 'MD',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'application/msword': 'DOC',
  'application/pdf': 'PDF',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
  'application/vnd.ms-powerpoint': 'PPT',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'application/vnd.ms-excel': 'XLS',
  'image/jpeg': 'JPEG',
  'image/jpg': 'JPG',
  'image/png': 'PNG',
  'image/bmp': 'BMP',
  'image/tiff': 'TIFF',
  'image/gif': 'GIF',
  'image/webp': 'WEBP',
};

export function UploadFilesDialog({
  isOpen,
  onClose,
  projectAttachments,
  onFilesUpload,
  onFileDelete,
  onFetchAttachments,
  isUploading = false,
  loadingAttachments = false,
  showToast,
  isWeb = false,
  planDetails,
  googleAuthConfig,
  projectId,
  onGoogleSyncStart,
}: UploadFilesDialogProps) {
  const { t, i18n } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);
  // Set when the user clicks a delete icon — opens the confirmation dialog; the
  // actual delete only runs once they confirm.
  const [fileToDelete, setFileToDelete] = useState<{ id: string; name: string } | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragDepth, setDragDepth] = useState(0);
  const [activeTab, setActiveTab] = useState<'upload' | 'google'>('upload');

  // Local uploading files state - shows files immediately with loading spinner
  const [localUploadingFiles, setLocalUploadingFiles] = useState<
    Array<{ id: string; fileName: string }>
  >([]);

  // Google Drive state
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [apisLoaded, setApisLoaded] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const authTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [selectedGoogleFiles, setSelectedGoogleFiles] = useState<GDriveFile[]>([]);
  const [validationResult, setValidationResult] = useState<FileValidationResult | null>(null);
  const [googleStep, setGoogleStep] = useState<'auth' | 'summary'>('auth');
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [protectFilesExpanded, setProtectFilesExpanded] = useState(false);
  const [isResyncing, setIsResyncing] = useState(false);

  // File limit selection state (inline, not separate dialog)
  const [showFileLimitSelection, setShowFileLimitSelection] = useState(false);
  const [filesToSelectFrom, setFilesToSelectFrom] = useState<FileForSelection[]>([]);
  const [selectedLimitFileIds, setSelectedLimitFileIds] = useState<Set<string>>(new Set());

  // Plan and limit calculations. "Files Per Project" can be a number or the
  // string 'unlimited' — when unlimited (or a non-free plan), no cap is enforced
  // so the free-plan limit UI is suppressed entirely.
  const isFreePlan = planDetails?.name?.toLowerCase() === 'free';
  const rawFileLimit = planDetails?.features?.find((f: any) => f.name === 'Files Per Project')
    ?.maxLimit;
  const fileLimitEnforced = isFreePlan && rawFileLimit !== 'unlimited';
  const limit = fileLimitEnforced ? (Number(rawFileLimit) || 10) : Number.POSITIVE_INFINITY;
  const totalAttachmentCount = projectAttachments.length + localUploadingFiles.length;
  const remainingSlots = fileLimitEnforced
    ? Math.max(0, limit - totalAttachmentCount)
    : Number.MAX_SAFE_INTEGER;
  const atLimit = fileLimitEnforced && totalAttachmentCount >= limit;

  // Filter attachments by source
  const manualAttachments = projectAttachments.filter((a) => !a.fromGoogle);
  const googleAttachments = projectAttachments.filter((a) => a.fromGoogle);

  // Fetch attachments when dialog opens
  useEffect(() => {
    if (isOpen && onFetchAttachments) {
      onFetchAttachments();
    }
  }, [isOpen, onFetchAttachments]);

  // Clear local uploading files once they appear in projectAttachments. The
  // server returns the name under `name` (not `fileName`), so resolve both
  // sides to the same normalized name — otherwise the placeholder never matches
  // the uploaded file and spins forever next to the finished upload.
  useEffect(() => {
    if (localUploadingFiles.length > 0) {
      const attachmentNames = new Set(projectAttachments.map(resolveAttachmentName));
      setLocalUploadingFiles((prev) =>
        prev.filter((f) => !attachmentNames.has(fixFilenameMojibake(f.fileName)))
      );
    }
  }, [projectAttachments]);

  // Load Google APIs only once the user actually opens the Google Drive tab —
  // not on dialog open — so the local-upload flow never pulls in Google.
  useEffect(() => {
    if (isOpen && activeTab === 'google' && googleAuthConfig && !apisLoaded) {
      loadGoogleApis(googleAuthConfig.API_KEY)
        .then(() => setApisLoaded(true))
        .catch((err) => {
          console.error('Failed to load Google APIs:', err);
          setError(t('uploadDialog.errGoogleLoad'));
        });
    }
  }, [isOpen, activeTab, googleAuthConfig, apisLoaded]);

  // Reset Google state when dialog closes, but try to auto-refresh token on open
  useEffect(() => {
    if (!isOpen) {
      setGoogleStep('auth');
      setSelectedGoogleFiles([]);
      setValidationResult(null);
      setError(null);
      setProtectFilesExpanded(false);
      setIsAuthenticating(false);
      // Don't clear access token - backend can mint fresh ones
      // Clear any pending auth timeout
      if (authTimeoutRef.current) {
        clearTimeout(authTimeoutRef.current);
        authTimeoutRef.current = null;
      }
    }
  }, [isOpen]);

  // Auto-fetch a fresh token from the backend ONLY when the user is on the
  // Google Drive tab (if already connected). Previously this ran on every dialog
  // open — hitting `gdrive/auth/token` even for plain local uploads.
  useEffect(() => {
    if (isOpen && activeTab === 'google' && googleAuthConfig && !accessToken) {
      googleDriveService
        .getFreshAccessToken()
        .then(({ accessToken: token }) => {
          setAccessToken(token);
        })
        .catch(() => {
          // Not connected yet - user will need to sign in
          setAccessToken(null);
        });
    }
  }, [isOpen, activeTab, googleAuthConfig]);

  const validateFileTypes = (
    files: FileList
  ): { validFiles: File[]; invalidFiles: File[]; oversizedFiles: File[]; emptyFiles: File[] } => {
    const validFiles: File[] = [];
    const invalidFiles: File[] = [];
    const oversizedFiles: File[] = [];
    const emptyFiles: File[] = [];

    Array.from(files).forEach((file) => {
      // Reject empty files
      if (file.size === 0) {
        emptyFiles.push(file);
        return;
      }

      // Check file size limit
      if (file.size > MAX_FILE_SIZE) {
        oversizedFiles.push(file);
        return;
      }

      // Get file extension
      const fileExtension = file.name.toLowerCase().split('.').pop();

      // Check by MIME type first, then fallback to extension for common cases
      if (
        (ALLOWED_FILE_TYPES as readonly string[]).includes(file.type) ||
        (fileExtension === 'md' && (file.type === '' || file.type === 'text/plain')) ||
        (fileExtension === 'txt' && file.type === '') ||
        (fileExtension === 'pptx' && file.type === '') ||
        (fileExtension === 'ppt' && file.type === '') ||
        (fileExtension === 'xlsx' && file.type === '') ||
        (fileExtension === 'xls' && file.type === '') ||
        (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension || '') &&
          file.type.startsWith('image/'))
      ) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file);
      }
    });

    return { validFiles, invalidFiles, oversizedFiles, emptyFiles };
  };

  const handleFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const { validFiles, invalidFiles, oversizedFiles, emptyFiles } = validateFileTypes(files);
      // Show error toast for empty files
      if (emptyFiles.length > 0 && showToast) {
        showToast({
          message: t(
            emptyFiles.length > 1 ? 'uploadDialog.toastEmptyFiles' : 'uploadDialog.toastEmptyFile'
          ),
          severity: 'error',
        });
      }

      // Show error toast for oversized files
      if (oversizedFiles.length > 0 && showToast) {
        showToast({
          message: t(
            oversizedFiles.length > 1
              ? 'uploadDialog.toastTooLargeFiles'
              : 'uploadDialog.toastTooLargeFile',
            { size: MAX_ATTACHMENT_SIZE_BYTES / 1024 / 1024 }
          ),
          severity: 'error',
        });
      }

      // Show error toast for invalid files (not supported)
      if (invalidFiles.length > 0 && showToast) {
        const supportedTypes = Object.values(FILE_TYPE_NAMES).join(', ');
        showToast({
          message: t(
            invalidFiles.length > 1
              ? 'uploadDialog.toastUnsupportedFiles'
              : 'uploadDialog.toastUnsupportedFile',
            { types: supportedTypes }
          ),
          severity: 'error',
        });
      }

      // Only allow up to the limit
      let allowedCount = limit - totalAttachmentCount;
      let filesToUpload = validFiles;
      if (fileLimitEnforced && allowedCount < validFiles.length) {
        filesToUpload = validFiles.slice(0, allowedCount);
        if (showToast) {
          showToast({
            message: t(
              allowedCount === 1 ? 'uploadDialog.toastLimitOne' : 'uploadDialog.toastLimitMany',
              { count: allowedCount, limit }
            ),
            severity: 'info',
          });
        }
      }

      if (filesToUpload.length > 0) {
        // Add files to local state immediately to show loading spinners.
        const newLocalFiles = filesToUpload.map((file, index) => ({
          id: `uploading-${Date.now()}-${index}`,
          fileName: file.name,
        }));
        const newLocalIds = new Set(newLocalFiles.map((f) => f.id));
        setLocalUploadingFiles((prev) => [...prev, ...newLocalFiles]);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

        try {
          // Per-file failures (e.g. the plan file-limit) are surfaced by the
          // upload hook itself; this catch only handles an outright rejection.
          await onFilesUpload(filesToUpload);
        } catch (error) {
          console.error('Failed to upload files:', error);
          if (showToast) {
            showToast({
              message: t('uploadDialog.toastUploadFailed'),
              info: t('uploadDialog.toastUploadFailedInfo'),
              severity: 'error',
            });
          }
        } finally {
          // Drop this batch's optimistic placeholders once the upload settles:
          // succeeded files now render from the refetched attachment list, and
          // failed ones disappear instead of spinning forever.
          setLocalUploadingFiles((prev) => prev.filter((f) => !newLocalIds.has(f.id)));
        }
      } else {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    }
  };

  const handleUploadButtonClick = () => {
    fileInputRef.current?.click();
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragOver) setIsDragOver(true);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragDepth((prev) => {
      const next = prev + 1;
      if (next === 1) setIsDragOver(true);
      return next;
    });
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragDepth((prev) => {
      const next = Math.max(prev - 1, 0);
      if (next === 0) setIsDragOver(false);
      return next;
    });
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    setDragDepth(0);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      await handleFileInputChange({ target: { files } } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  const handleDeleteFile = async (fileId: string) => {
    setDeletingFileId(fileId);
    try {
      await onFileDelete(fileId);
    } catch (error) {
      console.error('Failed to delete file:', error);
    } finally {
      setDeletingFileId(null);
    }
  };

  // ========== Google Drive Handlers ==========

  // Handle files selected from picker
  const handleFilesSelectedFromPicker = useCallback(
    (files: GDriveFile[]) => {
      // Check if selected files exceed remaining slots
      // Only show limit dialog if files exceed the remaining slots
      if (fileLimitEnforced && remainingSlots > 0 && files.length > remainingSlots) {
        // Show inline file limit selection
        setFilesToSelectFrom(
          files.map((f) => ({
            id: f.id,
            name: f.name,
            mimeType: f.mimeType,
            size: f.size,
          }))
        );
        setSelectedLimitFileIds(new Set());
        setShowFileLimitSelection(true);
        return;
      }

      // Process files normally (if within limit or not on free plan)
      processSelectedGoogleFiles(files);
    },
    [fileLimitEnforced, remainingSlots]
  );

  // Open picker with a specific token
  const handleOpenPickerWithToken = useCallback(
    (token: string) => {
      if (!googleAuthConfig) return;

      setIsPickerOpen(true);
      openGoogleFilePicker(
        token,
        googleAuthConfig.API_KEY,
        googleAuthConfig.APP_ID,
        (files) => {
          setIsPickerOpen(false);
          handleFilesSelectedFromPicker(files);
        },
        () => {
          setIsPickerOpen(false);
          // User cancelled, stay on current state
        }
      );
    },
    [googleAuthConfig, handleFilesSelectedFromPicker]
  );

  // Open Google File Picker
  const handleOpenPicker = useCallback(() => {
    if (!accessToken || !googleAuthConfig) return;
    handleOpenPickerWithToken(accessToken);
  }, [accessToken, googleAuthConfig, handleOpenPickerWithToken]);

  // Handle Sign in with Google via backend OAuth popup
  const handleSignInWithGoogle = useCallback(async () => {
    if (!googleAuthConfig || !apisLoaded) {
      setError(t('uploadDialog.errGoogleLoading'));
      return;
    }

    setIsAuthenticating(true);
    setError(null);

    try {
      // First try to get a fresh token from backend (user may already be connected)
      const { accessToken: freshToken } = await googleDriveService.getFreshAccessToken();
      setAccessToken(freshToken);
      setIsAuthenticating(false);
      handleOpenPickerWithToken(freshToken);
      return;
    } catch {
      // Not connected - proceed with OAuth popup flow
    }

    try {
      // Get the auth URL from the backend
      const redirectUrl = window.location.origin + '/oauth2-callback';
      const { url } = await googleDriveService.getAuthUrl(redirectUrl);

      // Open popup window to Google OAuth
      const popup = window.open(url, 'gdrive-auth', 'width=500,height=700,left=200,top=100');

      if (!popup) {
        setIsAuthenticating(false);
        setError(t('uploadDialog.errPopupBlocked'));
        return;
      }

      // Listen for postMessage from the popup callback page
      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;

        if (event.data?.type === 'GDRIVE_AUTH_SUCCESS') {
          window.removeEventListener('message', handleMessage);
          const token = event.data.accessToken;
          setAccessToken(token);
          setIsAuthenticating(false);
          handleOpenPickerWithToken(token);
        } else if (event.data?.type === 'GDRIVE_AUTH_ERROR') {
          window.removeEventListener('message', handleMessage);
          setIsAuthenticating(false);
          setError(t('uploadDialog.errSignIn'));
        }
      };

      window.addEventListener('message', handleMessage);

      // Poll to detect if popup was closed without completing auth
      const pollTimer = setInterval(() => {
        if (popup.closed) {
          clearInterval(pollTimer);
          // Give a short delay for the message to arrive
          setTimeout(() => {
            window.removeEventListener('message', handleMessage);
            setIsAuthenticating(false);
          }, 1000);
        }
      }, 500);
    } catch (err: any) {
      setIsAuthenticating(false);
      setError(err.message || t('uploadDialog.errAuthStart'));
    }
  }, [apisLoaded, googleAuthConfig, handleOpenPickerWithToken]);

  // Process selected Google files
  const processSelectedGoogleFiles = useCallback((files: GDriveFile[]) => {
    setSelectedGoogleFiles(files);
    const validation = validateGDriveFiles(files);
    setValidationResult(validation);
    setGoogleStep('summary');
  }, []);

  // Handle file selection from inline limit selection
  const handleLimitedFileSelection = useCallback(() => {
    const selectedFiles = filesToSelectFrom.filter((f) => selectedLimitFileIds.has(f.id));
    const gdriveFiles: GDriveFile[] = selectedFiles.map((f) => ({
      id: f.id,
      name: f.name,
      mimeType: f.mimeType,
      size: f.size || 0,
    }));
    processSelectedGoogleFiles(gdriveFiles);
    setShowFileLimitSelection(false);
    setFilesToSelectFrom([]);
    setSelectedLimitFileIds(new Set());
  }, [filesToSelectFrom, selectedLimitFileIds, processSelectedGoogleFiles]);

  // Handle toggle file in limit selection
  const handleToggleLimitFile = useCallback(
    (fileId: string) => {
      setSelectedLimitFileIds((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(fileId)) {
          newSet.delete(fileId);
        } else {
          // Only allow adding if under the limit
          if (newSet.size < remainingSlots) {
            newSet.add(fileId);
          }
        }
        return newSet;
      });
    },
    [remainingSlots]
  );

  // Handle back from limit selection
  const handleBackFromLimitSelection = useCallback(() => {
    setShowFileLimitSelection(false);
    setFilesToSelectFrom([]);
    setSelectedLimitFileIds(new Set());
  }, []);

  // Handle select more files - gets fresh token from backend or triggers sign-in
  const handleSelectMoreFiles = useCallback(async () => {
    if (!googleAuthConfig) return;

    // Try to get a fresh token from backend if we don't have one
    if (!accessToken) {
      try {
        const { accessToken: freshToken } = await googleDriveService.getFreshAccessToken();
        setAccessToken(freshToken);
        // Continue to open picker with fresh token below
      } catch {
        // Not connected - trigger full sign-in flow
        handleSignInWithGoogle();
        return;
      }
    }

    const tokenToUse = accessToken!;

    setIsPickerOpen(true);
    openGoogleFilePicker(
      tokenToUse,
      googleAuthConfig.API_KEY,
      googleAuthConfig.APP_ID,
      (files) => {
        setIsPickerOpen(false);
        // Add new files to existing selection (dedupe by id)
        const existingIds = new Set(selectedGoogleFiles.map((f) => f.id));
        const newFiles = files.filter((f) => !existingIds.has(f.id));
        const allFiles = [...selectedGoogleFiles, ...newFiles];

        // Check limit again - only show limit dialog if exceeds remaining slots
        if (fileLimitEnforced && remainingSlots > 0 && allFiles.length > remainingSlots) {
          setFilesToSelectFrom(
            allFiles.map((f) => ({
              id: f.id,
              name: f.name,
              mimeType: f.mimeType,
              size: f.size,
            }))
          );
          setSelectedLimitFileIds(new Set());
          setShowFileLimitSelection(true);
          return;
        }

        setSelectedGoogleFiles(allFiles);
        const validation = validateGDriveFiles(allFiles);
        setValidationResult(validation);
      },
      () => {
        setIsPickerOpen(false);
      }
    );
  }, [
    accessToken,
    selectedGoogleFiles,
    googleAuthConfig,
    fileLimitEnforced,
    remainingSlots,
    handleSignInWithGoogle,
  ]);

  // Handle sync button click
  const handleGoogleSync = useCallback(() => {
    if (
      !accessToken ||
      !validationResult ||
      validationResult.validFiles.length === 0 ||
      !onGoogleSyncStart
    )
      return;

    onGoogleSyncStart({
      folderId: 'selected-files',
      folderName: `${validationResult.validFiles.length} selected files`,
      accessToken,
      acceptedFilesCount: validationResult.validFiles.length,
      files: validationResult.validFiles.map((f) => ({
        id: f.id,
        name: f.name,
        mimeType: f.mimeType,
        size: f.size,
      })),
    });
    onClose();
  }, [accessToken, validationResult, onGoogleSyncStart, onClose]);

  // Handle re-sync of existing Google Drive files
  const handleResyncGoogleFiles = useCallback(async () => {
    if (!onGoogleSyncStart || googleAttachments.length === 0) return;
    debugger;

    setIsResyncing(true);
    setError(null);

    try {
      // Get a fresh access token from the backend
      let token = accessToken;
      if (!token) {
        const { accessToken: freshToken } = await googleDriveService.getFreshAccessToken();
        token = freshToken;
        setAccessToken(freshToken);
      }

      // Build file list from existing Google attachments
      const files = googleAttachments
        .filter((att) => att.gdriveId)
        .map((att) => ({
          id: att.gdriveId!,
          name: att.fileName,
          mimeType: att.mimeType || 'application/octet-stream',
          size: att.fileSize,
          attachmentId: att.id,
        }));

      if (files.length === 0) {
        setError(t('uploadDialog.errNoResync'));
        setIsResyncing(false);
        return;
      }

      onGoogleSyncStart({
        folderId: 'resync-files',
        folderName: `Re-sync ${files.length} files`,
        accessToken: token,
        acceptedFilesCount: files.length,
        files,
      });

      onClose();
    } catch {
      setError(t('uploadDialog.errResync'));
    } finally {
      setIsResyncing(false);
    }
  }, [accessToken, googleAttachments, onGoogleSyncStart, onClose]);

  // Handle back from summary to auth
  const handleGoogleBack = useCallback(() => {
    setGoogleStep('auth');
    setSelectedGoogleFiles([]);
    setValidationResult(null);
  }, []);

  // Check if Google Drive tab should be shown
  const showGoogleTab = isWeb && googleAuthConfig && onGoogleSyncStart;

  // Don't render dialog when picker is open
  if (isPickerOpen) {
    return null;
  }
  const handleClose = () => {
    setShowFileLimitSelection(false);
    setFilesToSelectFrom([]);
    setSelectedLimitFileIds(new Set());
    onClose();
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent
          data-testid="upload-files-dialog"
          dir={i18n.dir()}
          className={`${isWeb ? 'max-w-[85%]' : 'max-w-[50%]'} lg:w-[520px] mx-auto flex flex-col gap-4`}
          // Don't close on backdrop / outside click — an upload may be running.
          // Use the explicit close (X) button instead.
          onInteractOutside={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          {/* Show file limit selection content OR google summary OR normal dialog content */}
          {showFileLimitSelection && filesToSelectFrom.length > 0 ? (
            // File limit selection view - simplified header
            <>
              <DialogHeader className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <DialogTitle>{t('uploadDialog.limitReached')}</DialogTitle>
                </div>
                <button onClick={handleClose} className="dialog-close-button">
                  <Icon icon={X} color="#667085" strokeWidth={2.5} />
                </button>
              </DialogHeader>
              {renderFileLimitSelection()}
            </>
          ) : googleStep === 'summary' && validationResult ? (
            // Google Files Summary view - simplified header with icon
            <>
              <DialogHeader className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileCheckIcon />
                  <DialogTitle>{t('uploadDialog.filesSummary')}</DialogTitle>
                </div>
                <button onClick={onClose} className="dialog-close-button">
                  <Icon icon={X} color="#667085" strokeWidth={2.5} />
                </button>
              </DialogHeader>
              {renderGoogleSummaryStep()}
            </>
          ) : (
            // Normal dialog content
            <>
              <DialogHeader className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <DialogTitle>{t('uploadDialog.title')}</DialogTitle>
                </div>
                <button onClick={handleClose} className="dialog-close-button">
                  <Icon icon={X} color="#667085" strokeWidth={2.5} />
                </button>
              </DialogHeader>

              {/* File count and limit info */}
              {fileLimitEnforced ? (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    {t('uploadDialog.freeLimitNote', { limit })}
                  </p>
                  <div
                    className={`${atLimit ? 'text-warning-700 bg-warning-50 border-warning-200' : 'text-[#414651] bg-[#FAFAFA] border-[#E9EAEB]'} py-1 px-2 rounded-full text-xs font-medium border`}
                  >
                    {`${totalAttachmentCount}/${limit}`}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-600">{t('uploadDialog.contextHint')}</p>
              )}

              {/* Supported files info - above tabs */}
              <div className="w-full bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-blue-700">
                      {t('uploadDialog.supportedFiles')}{' '}
                      <span className="font-normal text-blue-600">
                        {t('uploadDialog.maxSize', {
                          size: MAX_ATTACHMENT_SIZE_BYTES / 1024 / 1024,
                        })}
                      </span>
                    </p>
                    <p className="text-xs text-blue-600 mt-0.5">{getSupportedFileTypesString()}</p>
                  </div>
                </div>
              </div>

              {/* Tabs for Upload vs Google Drive */}
              {showGoogleTab ? (
                <Tabs
                  value={activeTab}
                  onValueChange={(v: string) => setActiveTab(v as 'upload' | 'google')}
                  className="w-full bg-background p-3 rounded-2xl"
                >
                  <TabsList className="w-full grid grid-cols-2">
                    <TabsTrigger value="upload" className="flex items-center gap-2">
                      <Icon icon={FilePlus} size={16} strokeWidth={2} />
                      {t('uploadDialog.tabUpload')}
                    </TabsTrigger>
                    <TabsTrigger value="google" className="flex items-center gap-2">
                      <GoogleDriveIcon size={16} />
                      {t('uploadDialog.tabGoogleDrive')}
                    </TabsTrigger>
                  </TabsList>

                  {/* Upload Tab Content */}
                  <TabsContent value="upload" className="mt-4">
                    {renderUploadTabContent()}
                  </TabsContent>

                  {/* Google Drive Tab Content */}
                  <TabsContent value="google" className="mt-4">
                    {renderGoogleDriveTabContent()}
                  </TabsContent>
                </Tabs>
              ) : (
                // No tabs, just show upload content
                <div>{renderUploadTabContent()}</div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={!!fileToDelete}
        onOpenChange={(open) => {
          if (!open) setFileToDelete(null);
        }}
      >
        <DialogContent className="max-w-sm" dir={i18n.dir()}>
          <DialogHeader>
            <DialogTitle>{t('uploadDialog.deleteTitle')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 dark:text-gray-300 break-all">
            {t('uploadDialog.deleteBody', {
              name: fileToDelete?.name || t('uploadDialog.deleteBodyDefault'),
            })}
          </p>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setFileToDelete(null)}>
              {t('uploadDialog.cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={!!deletingFileId}
              onClick={async () => {
                const id = fileToDelete?.id;
                setFileToDelete(null);
                if (id) await handleDeleteFile(id);
              }}
            >
              {t('uploadDialog.delete')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );

  // ========== Render Functions ==========

  function renderUploadTabContent() {
    return (
      <div className="flex flex-col rounded-lg p-3 bg-background">
        <div className="max-h-[45vh] overflow-y-auto">
          {loadingAttachments ? (
            <div className="space-y-2">
              {[1, 2].map((index) => (
                <div
                  key={`skeleton-${index}`}
                  className="flex h-[70px] items-center gap-3 p-3 border border-gray-200 rounded-lg bg-white"
                >
                  <div className="flex-shrink-0">
                    <div className="w-[18px] h-[18px] bg-gray-200 rounded animate-pulse"></div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col gap-1">
                      <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4"></div>
                      <div className="h-3 bg-gray-200 rounded animate-pulse w-1/4"></div>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="w-[14px] h-[14px] bg-gray-200 rounded animate-pulse"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : manualAttachments.length === 0 && localUploadingFiles.length === 0 ? (
            // Show empty state or limit reached message when no files
            atLimit ? (
              <div className="flex flex-col items-center py-8 px-4 bg-gray-50 rounded-lg">
                <Icon
                  icon={FileSearch}
                  size={34}
                  strokeWidth={1.5}
                  color="#9CA3AF"
                  className="mb-3"
                />
                <span className="text-base font-medium text-gray-700 mb-1">
                  {t('uploadDialog.cannotUpload')}
                </span>
                <span className="text-sm text-gray-500 text-center">
                  {t('uploadDialog.limitReachedBody', { limit })}
                </span>
              </div>
            ) : (
              <div
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`flex flex-col items-center justify-center py-8 px-4 rounded-lg transition-colors ${
                  isDragOver ? 'border-2 border-dashed border-blue-400 bg-blue-50' : ''
                }`}
              >
                <div className="mb-3">
                  <Icon icon={FileSearch} size={34} strokeWidth={1.5} color="#252B37" />
                </div>
                <span className="text-base font-medium text-gray-700 mb-1">
                  {isDragOver
                    ? t('uploadDialog.dropHere')
                    : t('uploadDialog.uploadYourFiles')}
                </span>
                <span className="text-sm text-gray-500 text-center">
                  {isDragOver
                    ? t('uploadDialog.releaseToUpload')
                    : t('uploadDialog.proceedHint')}
                </span>
              </div>
            )
          ) : (
            <div
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`space-y-2 relative ${isDragOver ? 'bg-blue-50 border-2 border-dashed border-blue-400 rounded-lg p-2' : ''}`}
            >
              {isDragOver && (
                <div className="absolute inset-0 flex items-center justify-center bg-blue-50 bg-opacity-90 border-2 border-dashed border-blue-400 rounded-lg z-10 pointer-events-none">
                  <div className="text-center">
                    <Icon
                      icon={FileSearch}
                      size={34}
                      strokeWidth={1.5}
                      color="#3B82F6"
                      className="mx-auto mb-2"
                    />
                    <span className="text-base font-medium text-blue-700">
                      {t('uploadDialog.dropHere')}
                    </span>
                    <span className="block text-sm text-blue-600">
                      {t('uploadDialog.releaseToUpload')}
                    </span>
                  </div>
                </div>
              )}
              {/* Local uploading files - show with loading spinner */}
              {localUploadingFiles.map((file) => (
                <div
                  key={file.id}
                  className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg bg-white"
                >
                  <div className="relative flex-shrink-0">
                    <div className="w-[18px] h-[18px] border-2 border-gray-200 border-t-primary-600 rounded-full animate-spin"></div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col">
                      <div className="text-base font-medium text-gray-900 truncate">
                        {file.fileName?.substring(0, file.fileName.lastIndexOf('.')) ||
                          file.fileName}
                      </div>
                      <div className="text-sm text-gray-500">
                        {file.fileName?.substring(file.fileName.lastIndexOf('.') + 1)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {/* Already uploaded files */}
              {manualAttachments.map((attachment) => {
                const fileName = resolveAttachmentName(attachment);
                return (
                  <div
                    key={attachment.id}
                    className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 transition-colors"
                  >
                    <div className="relative flex-shrink-0">
                      {!attachment.isLoading && (
                        <Icon icon={FileSearch} size={18} strokeWidth={2} color="#029B8C" />
                      )}
                      {attachment.isLoading && (
                        <div className="w-[18px] h-[18px] border-2 border-gray-200 border-t-primary-600 rounded-full animate-spin"></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col">
                        <div className="text-base font-medium text-gray-900 truncate">
                          {fileName.substring(0, fileName.lastIndexOf('.')) || fileName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {fileName.substring(fileName.lastIndexOf('.') + 1)}
                        </div>
                      </div>
                    </div>
                    {!attachment.isLoading && (
                      <button
                        onClick={() => setFileToDelete({ id: attachment.id, name: fileName })}
                        disabled={deletingFileId === attachment.id || attachment.isLoading}
                        className="flex-shrink-0 p-1 text-red-500 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                      >
                        {deletingFileId === attachment.id ? (
                          <div className="w-3 h-3 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <Icon icon={Trash2} size={14} strokeWidth={1.5} />
                        )}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileInputChange}
          className="hidden"
          accept=".txt,.md,.docx,.doc,.pdf,.pptx,.ppt,.xlsx,.xls,.jpeg,.jpg,.png,.gif,.webp,text/plain,text/markdown,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/pdf,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,image/jpeg,image/jpg,image/png,image/gif,image/webp"
        />

        {/* Upload button - always shown, fixed at bottom */}
        <div className="flex justify-end pt-4 border-t border-gray-200 mt-4">
          <Button
            onClick={handleUploadButtonClick}
            disabled={isUploading || atLimit}
            variant="secondary-gray"
            size={isWeb ? 'sm' : 'xs'}
          >
            {manualAttachments.length > 0 || localUploadingFiles.length > 0
              ? t('uploadDialog.uploadNewFiles')
              : t('uploadDialog.uploadFiles')}
          </Button>
        </div>
      </div>
    );
  }

  function renderGoogleDriveTabContent() {
    // Show inline file limit selection
    if (showFileLimitSelection && filesToSelectFrom.length > 0) {
      return renderFileLimitSelection();
    }

    // Summary step
    if (googleStep === 'summary' && validationResult) {
      return renderGoogleSummaryStep();
    }

    // Show Google files list when files exist (similar to Upload tab pattern)
    if (googleAttachments.length > 0) {
      return (
        <div className="flex flex-col">
          {/* Auto-Sync note */}
          <div className="flex items-start gap-3 mb-4 p-3 ">
            <div className="flex-shrink-0 w-10 h-10 rounded bg-warning-50 flex items-center justify-center">
              <Icon icon={RefreshCw} size={20} color="#DC6803" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-600">{t('uploadDialog.autoSync')}</p>
              <p className="text-xs text-gray-800">{t('uploadDialog.autoSyncDesc')}</p>
            </div>
          </div>
          <div className="max-h-[45vh] overflow-y-auto">
            <div className="space-y-2">
              {googleAttachments.map((attachment) => (
                <div
                  key={attachment.id}
                  className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 transition-colors"
                >
                  <GoogleDriveIcon size={18} />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col">
                      <div className="text-base font-medium text-gray-900 truncate">
                        {attachment.fileName?.substring(0, attachment.fileName.lastIndexOf('.')) ||
                          attachment.fileName}
                      </div>
                      <div className="text-sm text-gray-500">
                        {attachment.fileName
                          ?.substring(attachment.fileName.lastIndexOf('.') + 1)
                          ?.toUpperCase()}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() =>
                      setFileToDelete({
                        id: attachment.id,
                        name: resolveAttachmentName(attachment),
                      })
                    }
                    disabled={deletingFileId === attachment.id}
                    className="flex-shrink-0 p-1 text-red-500 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                  >
                    {deletingFileId === attachment.id ? (
                      <div className="w-3 h-3 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <Icon icon={Trash2} size={14} strokeWidth={1.5} />
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Add more files / Re-sync buttons */}
          <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 mt-4">
            {/* <Button
              variant="secondary-gray"
              className="flex items-center gap-2 justify-center"
              onClick={handleResyncGoogleFiles}
              disabled={isResyncing || isAuthenticating}
            >
              {isResyncing ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-200 border-t-gray-600" />
              ) : (
                <Icon icon={RefreshCw} size={16} />
              )}
              <span>Re-sync</span>
            </Button> */}
            <Button
              variant="secondary-gray"
              className="flex items-center gap-2 justify-center"
              onClick={handleSignInWithGoogle}
              disabled={isAuthenticating || !apisLoaded || atLimit}
            >
              {isAuthenticating ? (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-200 border-t-gray-600" />
              ) : (
                <GoogleIcon />
              )}
              <span>
                {accessToken ? t('uploadDialog.selectFiles') : t('uploadDialog.signInGoogle')}
              </span>
            </Button>
          </div>
        </div>
      );
    }

    // Auth step (default) - no files yet
    return renderGoogleAuthStep();
  }

  // Render inline file limit selection
  function renderFileLimitSelection() {
    const isAtSelectionLimit = selectedLimitFileIds.size >= remainingSlots;

    return (
      <div className="flex flex-col">
        {/* Subtitle with counter */}
        <div className="flex items-start justify-between gap-4 mb-4">
          <p className="text-sm text-gray-600">
            {t('uploadDialog.limitSelectionNote', { limit, remaining: remainingSlots })}
          </p>
          <div className="text-[#414651] bg-[#FAFAFA] border-[#E9EAEB] py-1 px-2 rounded-full text-xs font-medium border whitespace-nowrap">
            {totalAttachmentCount}/{limit}
          </div>
        </div>

        {/* File list with checkboxes on the right */}
        <div className="max-h-[300px] overflow-y-auto space-y-2 mb-4">
          {filesToSelectFrom.map((file) => {
            const isSelected = selectedLimitFileIds.has(file.id);
            const isDisabled = !isSelected && isAtSelectionLimit;

            return (
              <div
                key={file.id}
                onClick={() => !isDisabled && handleToggleLimitFile(file.id)}
                className={`flex items-center gap-3 p-3 border rounded-lg transition-colors ${
                  isDisabled
                    ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                    : 'border-gray-200 bg-white hover:bg-gray-50 cursor-pointer'
                }`}
              >
                <div className="flex-shrink-0">
                  <Icon icon={FileText} size={18} strokeWidth={2} color="#029B8C" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                  <p className="text-xs text-gray-500 uppercase">
                    {file.name.split('.').pop()?.toUpperCase() || ''}
                  </p>
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => handleToggleLimitFile(file.id)}
                    disabled={isDisabled}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Action button */}
        <div className="flex justify-end pt-2 border-t border-gray-200">
          <Button
            variant="primary"
            onClick={handleLimitedFileSelection}
            disabled={selectedLimitFileIds.size === 0}
          >
            {t('uploadDialog.selectFilesBtn')}
          </Button>
        </div>
      </div>
    );
  }

  function renderGoogleAuthStep() {
    return (
      <div className="flex flex-col items-center">
        <h3 className="text-lg font-semibold text-gray-900 mt-4 mb-2">
          {t('uploadDialog.signInGoogle')}
        </h3>
        <p className="text-sm text-gray-600 text-center mb-4">
          {accessToken ? t('uploadDialog.signedInHint') : t('uploadDialog.authHint')}
        </p>

        {error && (
          <div className="w-full bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Sign in / Select files button */}
        {accessToken ? (
          <Button variant="secondary-gray" onClick={handleOpenPicker} disabled={atLimit}>
            {t('uploadDialog.selectFromDrive')}
          </Button>
        ) : (
          <Button
            variant="secondary-gray"
            className="flex items-center gap-2 justify-center"
            onClick={handleSignInWithGoogle}
            disabled={isAuthenticating || !apisLoaded || atLimit}
          >
            {isAuthenticating ? (
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-200 border-t-gray-600" />
            ) : (
              <GoogleIcon />
            )}
            <span>{t('uploadDialog.signInGoogle')}</span>
          </Button>
        )}
      </div>
    );
  }

  function renderGoogleSummaryStep() {
    const acceptedCount = validationResult?.validFiles.length || 0;
    const rejectedCount =
      (validationResult?.invalidFiles.length || 0) + (validationResult?.oversizedFiles.length || 0);
    const totalCount = selectedGoogleFiles.length;

    return (
      <div className="flex flex-col">
        {/* Subtitle */}
        <p className="text-sm text-gray-600 mb-4">{t('uploadDialog.summaryHint')}</p>

        {/* Selected files summary box */}
        <div className="w-full bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-4 h-4 text-gray-500" />
            <span className="text-xs text-gray-500">{t('uploadDialog.selectedFiles')}</span>
          </div>
          <p className="font-medium text-gray-900 mb-2">
            {t('uploadDialog.filesSelected', { count: totalCount })}
          </p>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-sm text-gray-700">{t('uploadDialog.totalAccepted')}</span>
              <span className="font-semibold text-gray-900">{acceptedCount}</span>
            </div>
            <div className="flex items-center gap-1">
              <XCircle className="w-4 h-4 text-red-500" />
              <span className="text-sm text-gray-700">{t('uploadDialog.totalRejected')}</span>
              <span className="font-semibold text-gray-900">{rejectedCount}</span>
            </div>
          </div>
        </div>

        {/* Supported files info */}
        <div className="w-full bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-blue-700">
                Supported files{' '}
                <span className="font-normal text-blue-600">
                  (Maximum file size {MAX_ATTACHMENT_SIZE_BYTES / 1024 / 1024}MB)
                </span>
              </p>
              <p className="text-xs text-blue-600 mt-0.5">{getSupportedFileTypesString()}</p>
            </div>
          </div>
        </div>

        {/* How we protect your files - Expandable */}
        <div className="w-full border border-gray-200 rounded-lg mb-4">
          <button
            className="w-full flex items-center justify-between p-3 text-start"
            onClick={() => setProtectFilesExpanded(!protectFilesExpanded)}
          >
            <span className="text-sm font-medium text-gray-900">
              {t('uploadDialog.protectTitle')}
            </span>
            {protectFilesExpanded ? (
              <ChevronUp className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            )}
          </button>
          {protectFilesExpanded && (
            <div className="px-3 pb-3">
              <ul className="text-xs text-gray-600 space-y-1 list-disc list-inside">
                <li>{t('uploadDialog.protect1')}</li>
                <li>{t('uploadDialog.protect2')}</li>
                <li>{t('uploadDialog.protect3')}</li>
              </ul>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex justify-end gap-3 pt-2 border-t border-gray-200">
          <Button variant="outline" onClick={handleGoogleBack}>
            {t('uploadDialog.back')}
          </Button>
          <Button variant="primary" onClick={handleGoogleSync} disabled={acceptedCount === 0}>
            {t('uploadDialog.title')}
          </Button>
        </div>
      </div>
    );
  }
}

// ========== Icon Components ==========

// Google Drive Icon SVG
const GoogleDriveIcon = ({ size = 40 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
    <path
      d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z"
      fill="#0066da"
    />
    <path
      d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z"
      fill="#00ac47"
    />
    <path
      d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z"
      fill="#ea4335"
    />
    <path
      d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z"
      fill="#00832d"
    />
    <path
      d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z"
      fill="#2684fc"
    />
    <path
      d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z"
      fill="#ffba00"
    />
  </svg>
);

// Google "G" Icon
const GoogleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M18.752 10.194C18.752 9.475 18.693 8.975 18.564 8.431H10.18V11.675H15.104C15.002 12.475 14.467 13.675 13.278 14.5L13.261 14.608L15.905 16.587L16.093 16.606C17.78 15.104 18.752 12.863 18.752 10.194Z"
      fill="#4285F4"
    />
    <path
      d="M10.1781 18.75C12.5881 18.75 14.6131 17.975 16.0931 16.606L13.2781 14.5C12.5231 15.025 11.5081 15.387 10.1781 15.387C7.81813 15.387 5.81313 13.894 5.09813 11.731L4.99438 11.74L2.23813 13.831L2.20312 13.928C3.67312 16.787 6.68563 18.75 10.1781 18.75Z"
      fill="#34A853"
    />
    <path
      d="M5.09937 11.731C4.91062 11.187 4.80188 10.6 4.80188 10C4.80188 9.4 4.91062 8.8125 5.09062 8.2687L5.08562 8.154L2.29437 6.03L2.20437 6.0725C1.59812 7.2587 1.25 8.5912 1.25 10C1.25 11.4087 1.59812 12.7412 2.20437 13.9275L5.09937 11.731Z"
      fill="#FBBC05"
    />
    <path
      d="M10.1781 4.6125C11.8544 4.6125 12.9856 5.3425 13.6306 5.9362L16.1506 3.525C14.6044 2.115 12.5881 1.25 10.1781 1.25C6.68563 1.25 3.67312 3.2125 2.20312 6.0725L5.08937 8.2687C5.81313 6.1062 7.81813 4.6125 10.1781 4.6125Z"
      fill="#EB4335"
    />
  </svg>
);

// File Checkmark Icon
const FileCheckIcon = () => (
  <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center">
    <FileText className="w-5 h-5 text-orange-600" />
  </div>
);
